/* eslint-disable no-restricted-syntax */

'use client';

import { ErrorCode, type FileRejection } from 'react-dropzone';
import { useEffect } from 'react';
import { FormInputErrors, Icon } from '~/components/primitives';
import type { MediaInput } from '@funkey/types/graphql/graphql';
import type { media } from '@funkey/shared';
import { useI18n } from '~/features/i18n';
import { useArray, useStateObject } from '~/utils/react';
import useUploadFiles from './useUploadFiles';
import { _DropzoneInput as DropzoneInput, type MimeType } from './DropzoneInput';
import Image from 'next/image';

export default function Dropzone({
    type,
    typeId = '',
    initialFiles,
    maxBytes = 5 * 1024 * 1024,
    mimeTypes = ['image/png', 'image/jpeg', 'video/mp4'],
    className,
    maxFiles = 10,
    minWidth,
    minHeight,
    onChange,
}: {
    type: media.MediaType;
    /** The ID of the entity it's linked to, based on the given `type` property
     * @default '' */
    typeId?: number | string;
    initialFiles: MediaInput[];
    /** @default false */
    maxBytes?: number;
    /** @default ['image/png', 'image/jpeg'] */
    mimeTypes?: Array<MimeType>;
    className?: string;
    /** @default 10 */
    maxFiles?: number;
    minWidth?: number;
    minHeight?: number;
    onChange?: (files: MediaInput[]) => void;
}) {
    const i18n = useI18n();

    const files = useArray<MediaInput>(initialFiles);
    const errors = useStateObject<string[]>([]);

    const uploadFiles = useUploadFiles();

    // This fixes some BS when initial files are loaded async and they are not set correctly in the internal state
    //
    // NOTE: this will cause sync issues if the parent onChange doesn't immediately set state and f.e.
    // calls `startTransition`
    useEffect(() => {
        if (initialFiles?.length && files.value.length === 0) {
            files.push(...initialFiles);
        }
    }, [initialFiles]);

    return (
        <>
            {files.value.length < maxFiles && (
                <DropzoneInput
                    i18n={i18n}
                    isUploading={uploadFiles.isPending}
                    mimeTypes={mimeTypes}
                    onDropAccepted={async (newFiles: File[]) => {
                        if (!newFiles.length) return;
                        errors.set([]);

                        if (newFiles.length + files.value.length > maxFiles) {
                            return void errors.set([
                                i18n._('The list of files cannot exceed {maxSize} items', {
                                    maxSize: maxFiles,
                                }),
                            ]);
                        }

                        const uploadedFiles = await uploadFiles.mutateAsync({
                            entity: {
                                attachableType: type,
                                attachableId: Number(typeId),
                            },
                            enrichedFiles: newFiles,
                        });

                        const mediaInputs: MediaInput[] = uploadedFiles.map(file => ({
                            ...file,
                            name: generateUniqueName(files.value, file.name),
                            attachableId: file.attachableId.toString(),
                        }));

                        files.setValue(prev => {
                            const updated = [...prev, ...mediaInputs];
                            onChange?.(updated);
                            return updated;
                        });
                    }}
                    // prettier-ignore
                    onDropRejected={(fileRejections: FileRejection[]) => {
                        errors.set(fileRejections.flatMap(rejection => rejection.errors.map(error => {
                            return `${rejection.file.name}: ${(() => {
                                switch (error.code) {
                                    // This error has a dynamic message, so we can't use generateFileErrorMessage
                                    // TODO: check using `dimensions-too-small`is enough
                                    case 'width-too-small':
                                    case 'height-too-small':
                                    case 'dimensions-too-small':
                                        return error.message;

                                    case ErrorCode.FileInvalidType:
                                        return i18n._('Invalid file type');
                                    case ErrorCode.FileTooLarge:
                                        return i18n._('File size is too large');
                                    case ErrorCode.FileTooSmall:
                                        return i18n._('File size is too small');
                                    case ErrorCode.TooManyFiles:
                                        return i18n._('Too many files');
                                    default:
                                        return i18n._('Oops, something went wrong!');
                                }
                            })()}`;
                        })));
                    }}
                    maxBytes={maxBytes}
                    maxFiles={maxFiles}
                    minWidth={minWidth}
                    minHeight={minHeight}
                    className={[errors.value.length > 0 && 'border-red-300', className]}
                />
            )}
            <div className="col-span-2">
                <FormInputErrors className="mt-3" errors={errors.value} />

                {!!files.value.length && (
                    <div className="mt-3 flex flex-col gap-3">
                        {files.value.map((file: MediaInput) => (
                            <div className="group flex items-center gap-x-4" key={file.name}>
                                <a
                                    target="_blank"
                                    href={file.path}
                                    rel="noopener noreferrer"
                                    className="relative grid h-[80px] w-[100px] place-items-center overflow-hidden rounded-xl"
                                >
                                    {!file.type.includes('video') ? (
                                        <Image
                                            className="absolute left-1/2 top-1/2 z-1 -translate-x-1/2 -translate-y-1/2 rounded-xl"
                                            src={file.path}
                                            alt={file.name ?? file.originalName}
                                            height={80}
                                            width={100}
                                        />
                                    ) : (
                                        <div className="flex">
                                            <Icon name="play" />
                                        </div>
                                    )}
                                </a>

                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={file.path}
                                    className="flex flex-col transition-all"
                                >
                                    <span className="text-sm font-medium text-gray-900">
                                        <span className="group-hover:text-secondary">
                                            {file.originalName ?? file.name}
                                        </span>
                                        {!file.originalName && (
                                            <span className="ml-6 inline-block rounded-lg bg-green-500 px-2 py-1 text-xs text-white">
                                                new
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-xs text-gray-600">
                                        {!!file.size && bytesToSize(file.size)}
                                    </span>
                                </a>

                                <button
                                    type="button"
                                    className="ml-auto flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                                    disabled={uploadFiles.isPending}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();

                                        files.setValue(prevFiles => {
                                            const updatedFiles = prevFiles.filter(
                                                f => f.name !== file.name,
                                            );
                                            onChange?.(updatedFiles);
                                            return updatedFiles;
                                        });
                                    }}
                                >
                                    <Icon name="close" className="h-5 w-5" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* TODO: Show dropped files, that are not in the main state yet */}
            </div>
        </>
    );
}

const generateUniqueName = (existingFiles: MediaInput[], fileName: string) => {
    const baseName = fileName.substring(0, fileName.lastIndexOf('.'));
    const extension = fileName.substring(fileName.lastIndexOf('.'));

    const generateUniqueNameRecursive = (
        fileName: string,
        baseName: string,
        extension: string,
        index: number,
    ) => {
        const exists =
            existingFiles.some(file => file.name === fileName) ||
            existingFiles.some(file => file.originalName === fileName);
        if (exists) {
            const newFileName = `${baseName} (${index})${extension}`;
            return generateUniqueNameRecursive(newFileName, baseName, extension, index + 1);
        }
        return fileName;
    };

    return generateUniqueNameRecursive(fileName, baseName, extension, 1);
};

const bytesToSize = (bytes: number) => {
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
    if (bytes <= 0) {
        return '0 Byte';
    }
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = Math.round(bytes / 1024 ** i);
    return `${size} ${sizes[i]}`;
};
